import { useEffect, useState, useCallback } from "react";
import { api, apiError, getToken, API } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "../components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { FileText, CheckCircle, XCircle, CurrencyInr, ArrowsClockwise, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PartnerApplications() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("pending");
  const [detail, setDetail] = useState(null);
  const [linkAmount, setLinkAmount] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/partner-applications", { params: { status: tab === "all" ? "" : tab } }).then((r) => setRows(r.data));
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const openDetail = (a) => { setDetail(a); setLinkAmount(String(a.onboarding_fee ?? "")); };

  const sendLink = async () => {
    setLinkBusy(true);
    try {
      const { data } = await api.post(`/partner-applications/${detail.id}/onboarding/send-link`, { amount: Number(linkAmount) });
      toast.success(`Payment link ₹${data.onboarding_link.amount} sent${data.notified_phone ? " via SMS" : ""}${data.notified_email ? " & email" : ""}`);
      const upd = { ...detail, onboarding_link: data.onboarding_link };
      setDetail(upd); setRows((rs) => rs.map((r) => r.id === upd.id ? { ...r, onboarding_link: data.onboarding_link } : r));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLinkBusy(false); }
  };

  const refreshLink = async () => {
    setLinkBusy(true);
    try {
      const { data } = await api.post(`/partner-applications/${detail.id}/onboarding/refresh`);
      const upd = { ...detail, onboarding_fee_paid: data.onboarding_fee_paid, onboarding_link: data.onboarding_link };
      setDetail(upd); setRows((rs) => rs.map((r) => r.id === upd.id ? { ...r, ...upd } : r));
      toast.success(data.onboarding_fee_paid ? "Onboarding fee is paid ✓" : `Status: ${data.onboarding_link?.status || "pending"}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLinkBusy(false); }
  };

  const approve = async (a) => {
    try {
      const { data } = await api.post(`/partner-applications/${a.id}/approve`, { create_login: true });
      if (data.activation_email_sent) toast.success(`Approved! Activation email sent to ${data.login_email}`);
      else toast.success(`Approved! Login: ${data.login_email}${data.temp_password ? ` · Temp password: ${data.temp_password}` : ""}`);
      setDetail(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const reject = async (a) => {
    try {
      await api.post(`/partner-applications/${a.id}/reject`);
      toast.success("Application rejected");
      setDetail(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const docUrl = (a, d) => `${API}/partner-applications/${a.id}/documents/${d.id}?auth=${getToken()}`;

  return (
    <>
      <PageHeader title="Partner Applications" subtitle="Review & approve partner sign-ups" />
      <div className="p-8 space-y-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-sm" data-testid="app-tabs">
            {["pending", "approved", "rejected", "all"].map((t) =>
              <TabsTrigger key={t} value={t} className="rounded-sm capitalize" data-testid={`apptab-${t}`}>{t}</TabsTrigger>)}
          </TabsList>
        </Tabs>
        <div className="border border-border rounded-sm bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead>Name</TableHead><TableHead>Category</TableHead>
                <TableHead>Contact</TableHead><TableHead>City</TableHead>
                <TableHead>Docs</TableHead><TableHead>Onboarding Fee</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="applications-table">
              {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No applications</TableCell></TableRow>}
              {rows.map((a) => (
                <TableRow key={a.id} className="hover:bg-secondary/30 cursor-pointer" onClick={() => openDetail(a)}>
                  <TableCell className="font-semibold">{a.name}</TableCell>
                  <TableCell>{a.category || "—"}</TableCell>
                  <TableCell className="text-sm"><div>{a.email}</div><div className="text-xs text-muted-foreground">{a.phone}</div></TableCell>
                  <TableCell>{a.city || "—"}</TableCell>
                  <TableCell><span className="text-xs flex items-center gap-1 text-muted-foreground"><FileText size={12} /> {a.documents?.length || 0}</span></TableCell>
                  <TableCell>
                    {a.onboarding_fee_paid
                      ? <span className="text-xs font-semibold text-green-600 flex items-center gap-1" data-testid={`fee-paid-${a.id}`}><CheckCircle size={13} weight="fill" /> Paid</span>
                      : <span className="text-xs text-amber-600 font-medium" data-testid={`fee-unpaid-${a.id}`}>₹{a.onboarding_fee} {a.onboarding_link ? "· link sent" : "· unpaid"}</span>}
                  </TableCell>
                  <TableCell><StatusBadge value={a.status === "pending" ? "requested" : (a.status === "approved" ? "complete" : "rejected")} /></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" className="rounded-sm" onClick={(e) => { e.stopPropagation(); openDetail(a); }} data-testid={`app-view-${a.id}`}>Review</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="application-detail">
          <SheetHeader><SheetTitle className="font-heading">{detail?.name}</SheetTitle></SheetHeader>
          {detail && (
            <div className="py-6 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[["Email", detail.email], ["Phone", detail.phone], ["Address", detail.address],
                  ["Pincode", detail.pincode], ["City", detail.city], ["State", detail.state],
                  ["Contact", detail.contact_person], ["Experience", detail.experience],
                  ["GSTIN", detail.gstin], ["MSME", detail.msme],
                  ["Email Verified", detail.email_verified ? "Yes ✓" : "No"]].map(([l, v]) => (
                  <div key={l} className="border-b border-border pb-2"><p className="label-caps">{l}</p><p className="font-medium mt-0.5 break-words">{v || "—"}</p></div>
                ))}
              </div>

              {detail.services_offered?.length > 0 && (
                <div>
                  <p className="label-caps mb-2">Services Offered</p>
                  <div className="flex flex-wrap gap-1.5">
                    {detail.services_offered.map((s, i) => <span key={i} className="text-xs bg-secondary px-2 py-1 rounded-sm">{s.category} · {s.service}</span>)}
                  </div>
                </div>
              )}

              {detail.service_areas?.length > 0 && (
                <div>
                  <p className="label-caps mb-2">Service Areas</p>
                  <div className="space-y-1.5">
                    {detail.service_areas.map((a, i) => (
                      <div key={i} className="text-sm"><span className="font-semibold">{a.city}:</span> <span className="text-muted-foreground">{a.pincodes?.map((p) => p.pincode).join(", ") || "—"}</span></div>
                    ))}
                  </div>
                </div>
              )}

              {detail.bank && (detail.bank.account_number || detail.bank.bank_name) && (
                <div>
                  <p className="label-caps mb-2">Bank Details</p>
                  <div className="text-sm border border-border rounded-sm p-3 space-y-1">
                    <div>{detail.bank.bank_name || "—"}</div>
                    <div className="text-muted-foreground">A/C: {detail.bank.account_number || "—"} · IFSC: {detail.bank.ifsc || "—"}</div>
                    {detail.bank.account_holder && <div className="text-muted-foreground">Holder: {detail.bank.account_holder}</div>}
                  </div>
                </div>
              )}

              {detail.notes && <div className="text-sm"><p className="label-caps">Notes</p><p className="mt-1">{detail.notes}</p></div>}

              <div className="border border-border rounded-sm p-3" data-testid="onboarding-fee-section">
                <div className="flex items-center justify-between">
                  <p className="label-caps flex items-center gap-1.5"><CurrencyInr size={14} weight="bold" /> Onboarding Fee</p>
                  {detail.onboarding_fee_paid
                    ? <span className="text-xs font-semibold text-green-600 flex items-center gap-1"><CheckCircle size={13} weight="fill" /> Paid</span>
                    : <span className="text-xs text-amber-600 font-medium">{detail.onboarding_link ? `Link ${detail.onboarding_link.status || "sent"}` : "Unpaid"}</span>}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Auto-computed for {detail.onboarding_tier === "cleaning" ? "cleaning" : "appliance repair"}: ₹{detail.onboarding_fee}. Editable below.</p>
                {detail.onboarding_link?.short_url && (
                  <a href={detail.onboarding_link.short_url} target="_blank" rel="noreferrer" className="text-xs text-primary underline break-all block mt-2" data-testid="onboarding-link-url">{detail.onboarding_link.short_url}</a>
                )}
                {!detail.onboarding_fee_paid && (
                  <div className="flex items-end gap-2 mt-3">
                    <div className="flex-1">
                      <Label className="label-caps">Amount (₹)</Label>
                      <Input type="number" min={0} value={linkAmount} onChange={(e) => setLinkAmount(e.target.value)} className="rounded-sm mt-1 h-9" data-testid="onboarding-amount-input" />
                    </div>
                    <Button size="sm" className="rounded-sm gap-1.5 h-9" onClick={sendLink} disabled={linkBusy || !linkAmount} data-testid="send-onboarding-link">
                      <PaperPlaneTilt size={14} weight="bold" /> {detail.onboarding_link ? "Resend" : "Send Link"}
                    </Button>
                  </div>
                )}
                {detail.onboarding_link && !detail.onboarding_fee_paid && (
                  <Button variant="outline" size="sm" className="rounded-sm gap-1.5 h-8 mt-2 w-full" onClick={refreshLink} disabled={linkBusy} data-testid="refresh-onboarding-link">
                    <ArrowsClockwise size={13} weight="bold" /> Check payment status
                  </Button>
                )}
              </div>

              <div>
                <p className="label-caps mb-2">Documents</p>
                {(!detail.documents || detail.documents.length === 0) && <p className="text-sm text-muted-foreground">No documents uploaded.</p>}
                <div className="space-y-2">
                  {detail.documents?.map((d) => (
                    <a key={d.id} href={docUrl(detail, d)} target="_blank" rel="noreferrer"
                      className="flex items-center gap-2 border border-border rounded-sm p-2 text-sm hover:bg-secondary transition-colors duration-200" data-testid={`app-doc-${d.id}`}>
                      <FileText size={16} className="text-primary" />
                      <span className="text-xs font-semibold uppercase text-primary w-16 shrink-0">{d.doc_type || "doc"}</span>
                      <span className="flex-1 truncate">{d.original_filename}</span>
                    </a>
                  ))}
                </div>
              </div>
              <StatusBadge value={detail.status === "pending" ? "requested" : (detail.status === "approved" ? "complete" : "rejected")} />
            </div>
          )}
          {detail?.status === "pending" && isAdmin && (
            <SheetFooter>
              <Button variant="outline" className="rounded-sm text-destructive gap-1.5" onClick={() => reject(detail)} data-testid="app-reject">
                <XCircle size={16} /> Reject
              </Button>
              <Button className="rounded-sm gap-1.5" onClick={() => approve(detail)} data-testid="app-approve">
                <CheckCircle size={16} weight="bold" /> Approve & Create Partner
              </Button>
            </SheetFooter>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
